import {DatePicker, Empty, Radio, Select, Spin, Table, Tabs} from "antd";
import {ColumnsType} from "antd/es/table";
import dayjs, {Dayjs} from "dayjs";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Link, useSearchParams} from "react-router-dom";
import {VisArea, VisAxis, VisBulletLegend, VisCrosshair, VisLine, VisStackedBar, VisTooltip, VisXYContainer} from "@unovis/react";
import {CurveType} from "@unovis/ts";
import {AiUsageServiceContext, OrganizationServiceContext} from "../../Contexts";
import AiTier from "../../domain/AiTier";
import Organization, {effectiveAiCreditLimit} from "../../domain/Organization";
import QueryOptions from "../../sal-ui/QueryOptions";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";
import AiUsageBreakdownRow from "../../domain/AiUsageBreakdownRow";
import AiUsageDailyGroupedPoint from "../../domain/AiUsageDailyGroupedPoint";
import AiUsageDailyPoint from "../../domain/AiUsageDailyPoint";
import AiUsageSummary from "../../domain/AiUsageSummary";
import {AiUsageDailyGroupBy, AiUsageTimePeriod} from "../../service/AiUsageService";
import {formatAiExtractorType} from "../../domain/AiExtractor";
import {formatVulSourceType} from "../../domain/VulSourceType";
import {formatCompactInteger, formatCredits, formatDurationMs, formatInteger} from "../../utils/FormatUtils";
import * as styles from "./AiUsageOverview.module.css";
import CreditsValue from "../common/CreditsValue";
import {FormPersister} from "../../sal-ui/FormPersister";
import {DateRangePresetId, parseFilterDate, resolveDateRangePresetValue} from "../../utils/DateRangePresets";
import {createDateRangePickerPresets, takePendingDatePresetId} from "../../utils/DateRangePresetPicker";

type ChartMetric = "requestCount" | "apiCallCount" | "webSearchCallCount" | "tokens" | "price";
type ChartGroupBy = "type" | "model" | "organization";
type ChartType = "bar" | "line" | "area";
type SourceBreakdownGroupBy = "source" | "source_organization";

const DAY_MS = 86_400_000;
const PERSISTENT_IDENT = "AiUsageOverview";

const chartMetricOptions: { value: ChartMetric; label: string }[] = [
    {value: "requestCount", label: "Requests"},
    {value: "apiCallCount", label: "API calls"},
    {value: "webSearchCallCount", label: "Web search"},
    {value: "tokens", label: "Tokens"},
    {value: "price", label: "Credits"},
];

const timePeriodOptions: { value: AiUsageTimePeriod; label: string }[] = [
    {value: "day", label: "Day"},
    {value: "month", label: "Month"},
];

const chartTypeOptions: { value: ChartType; label: string }[] = [
    {value: "bar", label: "Bar"},
    {value: "line", label: "Line"},
    {value: "area", label: "Area"},
];

const groupByOptions: { value: ChartGroupBy; label: string }[] = [
    {value: "model", label: "By model"},
    {value: "organization", label: "By organization"},
];

const groupByOptionsForTokens: { value: ChartGroupBy; label: string }[] = [
    {value: "type", label: "By type"},
    ...groupByOptions,
];

const tokenTypeColors = {
    inputNonCached: "#1677ff",
    inputCached: "#95de64",
    output: "#ffc53d",
};

const tokenTypeLegendItems = [
    {name: "Input (non-cached)", color: tokenTypeColors.inputNonCached},
    {name: "Input (cached)", color: tokenTypeColors.inputCached},
    {name: "Output", color: tokenTypeColors.output},
];

const PALETTE = ["#1677ff", "#95de64", "#ffc53d", "#ff7a45", "#b37feb", "#5cdbd3", "#ff85c0", "#ffd666", "#69b1ff", "#a0d911"];
const GROUPED_METRIC_SUFFIX: Record<ChartMetric, string> = {
    requestCount: ":requests",
    apiCallCount: ":apiCalls",
    webSearchCallCount: ":webSearch",
    tokens: ":tokens",
    price: ":price",
};
const chartDateFormats = {
    day: {full: "YYYY-MM-DD", tick: "MMM D"},
    month: {full: "MMMM YYYY", tick: "MMM YYYY"},
} as const;
type GroupedChartRow = { date: string } & Record<string, number | string>;

interface Props {
    organizationId?: string;
    showOrganizationBreakdown?: boolean;
    aiTier?: AiTier;
    aiCreditLimit?: number;
    onMonthlyCreditUsageChange?: (usage?: AiCreditUsage) => void;
}

interface AiUsageFilterValues {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    dateRangePresetId?: DateRangePresetId;
    organizationId?: string;
    period: AiUsageTimePeriod;
}

export type AiCreditUsage = {
    used: number;
    limit: number;
};

function AiUsageOverview(props: Props) {
    const aiUsageService = useContext(AiUsageServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const filterFormPersister = useMemo(() => new FormPersister(aiUsagePersistentIdent(props.organizationId)), [props.organizationId]);
    const [initialFilterValues] = useState(() =>
        loadAiUsageFilterValues(searchParams, filterFormPersister, props.showOrganizationBreakdown)
    );
    const defaultGroupBy: ChartGroupBy = props.showOrganizationBreakdown ? "organization" : "model";

    const [summary, setSummary] = useState<AiUsageSummary>();
    const [daily, setDaily] = useState<AiUsageDailyPoint[]>([]);
    const [organizationBreakdown, setOrganizationBreakdown] = useState<AiUsageBreakdownRow[]>([]);
    const [modelBreakdown, setModelBreakdown] = useState<AiUsageBreakdownRow[]>([]);
    const [purposeBreakdown, setPurposeBreakdown] = useState<AiUsageBreakdownRow[]>([]);
    const [aiExtractorBreakdown, setAiExtractorBreakdown] = useState<AiUsageBreakdownRow[]>([]);
    const [aiReportSummarizerBreakdown, setAiReportSummarizerBreakdown] = useState<AiUsageBreakdownRow[]>([]);
    const [sourceBreakdown, setSourceBreakdown] = useState<AiUsageBreakdownRow[]>([]);
    const [sourceOrganizationBreakdown, setSourceOrganizationBreakdown] = useState<AiUsageBreakdownRow[]>([]);
    const [sourceBreakdownGroupBy, setSourceBreakdownGroupBy] = useState<SourceBreakdownGroupBy>("source");
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(initialFilterValues.organizationId);
    const [range, setRange] = useState<[Dayjs, Dayjs]>([initialFilterValues.dateFrom, initialFilterValues.dateTo]);
    const [dateRangePresetId, setDateRangePresetId] = useState<DateRangePresetId | undefined>(initialFilterValues.dateRangePresetId);
    const [timePeriod, setTimePeriod] = useState<AiUsageTimePeriod>(initialFilterValues.period);
    const pendingDatePresetIdRef = useRef<DateRangePresetId>();
    const [loading, setLoading] = useState(true);
    const [chartMetric, setChartMetric] = useState<ChartMetric>("tokens");
    const [chartType, setChartType] = useState<ChartType>("bar");
    const [chartGroupBy, setChartGroupBy] = useState<ChartGroupBy>(defaultGroupBy);
    const [dailyGrouped, setDailyGrouped] = useState<AiUsageDailyGroupedPoint[]>([]);
    const [monthlyCreditsUsed, setMonthlyCreditsUsed] = useState<number | undefined>();
    const [previousPrice, setPreviousPrice] = useState<number | undefined>();
    const [sourceBreakdownLoading, setSourceBreakdownLoading] = useState(false);
    const reloadRequestId = useRef(0);
    const dailyGroupedRequestId = useRef(0);
    const sourceBreakdownRequestId = useRef(0);

    const effectiveOrgId = selectedOrgId || props.organizationId;
    const selectedOrganization = selectedOrgId
        ? organizations.find(o => o.id === selectedOrgId)
        : undefined;
    const monthlyAiTierCreditLimit = selectedOrganization
        ? effectiveAiCreditLimit(selectedOrganization)
        : props.aiCreditLimit ?? props.aiTier?.maxCreditsPerMonth;

    const dateFromStr = range[0].format("YYYY-MM-DD");
    const dateToStr = range[1].format("YYYY-MM-DD");

    const needsGroupedData = chartGroupBy === "model" || chartGroupBy === "organization";

    useEffect(() => {
        if (props.showOrganizationBreakdown) {
            organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
                .then(result => setOrganizations(result.data));
        }
    }, [organizationService, props.showOrganizationBreakdown]);

    useEffect(() => {
        const filterValues = {
            dateFrom: range[0],
            dateTo: range[1],
            dateRangePresetId,
            period: timePeriod,
            organizationId: props.showOrganizationBreakdown ? selectedOrgId : undefined
        };
        const params: Record<string, string> = dateRangePresetId
            ? {range: dateRangePresetId}
            : {from: dateFromStr, to: dateToStr};

        if (timePeriod !== "day") {
            params.period = timePeriod;
        }

        if (filterValues.organizationId) {
            params.organizationId = filterValues.organizationId;
        }

        filterFormPersister.saveState(filterValues);
        setSearchParams(params, {replace: true});
        reload();
    }, [effectiveOrgId, dateFromStr, dateToStr, dateRangePresetId, timePeriod]);

    useEffect(() => {
        reloadSourceBreakdown();
    }, [effectiveOrgId, dateFromStr, dateToStr, sourceBreakdownGroupBy]);

    useEffect(() => {
        let cancelled = false;

        setMonthlyCreditsUsed(undefined);

        if (monthlyAiTierCreditLimit === undefined || !effectiveOrgId) {
            return;
        }

        aiUsageService.getSummary({
            dateFrom: dayjs().startOf("month").format("YYYY-MM-DD"),
            dateTo: dayjs().format("YYYY-MM-DD"),
            organizationId: effectiveOrgId
        }).then(s => {
            if (!cancelled) {
                setMonthlyCreditsUsed(s.price);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [monthlyAiTierCreditLimit, effectiveOrgId]);

    useEffect(() => {
        props.onMonthlyCreditUsageChange?.(
            monthlyAiTierCreditLimit !== undefined && monthlyCreditsUsed !== undefined
                ? {used: monthlyCreditsUsed, limit: monthlyAiTierCreditLimit}
                : undefined
        );
    }, [monthlyAiTierCreditLimit, monthlyCreditsUsed, props.onMonthlyCreditUsageChange]);

    useEffect(() => {
        const requestId = ++dailyGroupedRequestId.current;

        setDailyGrouped([]);

        if (needsGroupedData) {
            const query = {
                dateFrom: dateFromStr,
                dateTo: dateToStr,
                period: timePeriod,
                organizationId: effectiveOrgId
            };

            aiUsageService.getDailyGrouped(query, chartGroupBy as AiUsageDailyGroupBy)
                .then(rows => {
                    if (dailyGroupedRequestId.current === requestId) {
                        setDailyGrouped(rows);
                    }
                });
        }
    }, [chartGroupBy, needsGroupedData, effectiveOrgId, dateFromStr, dateToStr, timePeriod]);

    function onChartMetricChange(metric: ChartMetric) {
        setChartMetric(metric);

        if (metric !== "tokens" && chartGroupBy === "type") {
            setChartGroupBy(defaultGroupBy);
        }
    }

    const groupedLabels = useMemo(
        () => [...new Set(dailyGrouped.map(p => p.label))],
        [dailyGrouped]
    );

    const groupedLegendItems = useMemo(
        () => groupedLabels.map((label, i) => ({name: label, color: PALETTE[i % PALETTE.length]})),
        [groupedLabels]
    );

    // Pivot grouped data into per-date rows for Unovis series (one y accessor per label).
    const groupedChartData = useMemo(() => {
        const byDate = new Map<string, GroupedChartRow>();

        dailyGrouped.forEach(p => {
            if (!byDate.has(p.date)) {
                byDate.set(p.date, {date: p.date});
            }

            const row = byDate.get(p.date)!;

            row[groupedMetricKey(p.label, "tokens")] = p.totalTokens;
            row[groupedMetricKey(p.label, "requestCount")] = p.requestCount;
            row[groupedMetricKey(p.label, "apiCallCount")] = p.apiCallCount;
            row[groupedMetricKey(p.label, "webSearchCallCount")] = p.webSearchCallCount;
            row[groupedMetricKey(p.label, "price")] = p.price;
        });

        return [...byDate.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, values]) => values);
    }, [dailyGrouped]);

    const loaded = !loading && summary !== undefined;
    const hasData = loaded && (summary.requestCount > 0 || daily.length > 0);
    const showTrendChart = timePeriod === "month" ? daily.length > 0 : daily.length > 1;

    return (
        <Spin spinning={loading}>
            <div className={styles.filters}>
                <DatePicker.RangePicker
                    allowClear={false}
                    value={range}
                    onChange={value => {
                        const pendingDatePresetId = takePendingDatePresetId(pendingDatePresetIdRef);

                        if (value?.[0] && value?.[1]) {
                            setRange([value[0], value[1]]);
                            setDateRangePresetId(pendingDatePresetId);
                        }
                    }}
                    presets={createDateRangePickerPresets(dateRangePresetId, pendingDatePresetIdRef, (presetId, presetRange) => {
                        setRange(presetRange);
                        setDateRangePresetId(presetId);
                    })}
                />
                {props.showOrganizationBreakdown && organizations.length > 0 && (
                    <Select
                        allowClear
                        placeholder="All organizations"
                        value={selectedOrgId}
                        onChange={setSelectedOrgId}
                        style={{minWidth: 200}}
                        showSearch
                        optionFilterProp="label"
                        options={organizations.map(o => ({value: o.id!, label: o.name!}))}
                    />
                )}
                <div className={styles.timePeriodFilter}>
                    <span className={styles.filterControlLabel}>Group dates by</span>
                    <Radio.Group
                        size="small"
                        optionType="button"
                        buttonStyle="solid"
                        value={timePeriod}
                        onChange={e => setTimePeriod(e.target.value)}
                        options={timePeriodOptions}
                    />
                </div>
            </div>

            {loaded && !hasData && (
                <Empty description="No AI usage data found for the selected period." style={{margin: "48px 0"}}/>
            )}

            {hasData && (
                <>
                    <div className={styles.summaryGroups}>
                        {summaryCardGroups().map(group => (
                            <div key={group.label} className={styles.summaryGroup}>
                                <div className={styles.summaryGroupLabel}>{group.label}</div>
                                <div className={styles.summaryRow}>
                                    {group.cards.map(card => (
                                        <div key={card.key} className={styles.summaryCard}>
                                            <div className={styles.summaryLabel}>{card.label}</div>
                                            <div className={styles.summaryValue} title={card.title}>
                                                {card.value}
                                                {card.delta && (
                                                    <span className={card.delta.direction === "up" ? styles.deltaUp : styles.deltaDown}>
                                                        {card.delta.text}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {showTrendChart && (
                        <div className={styles.section}>
                            <div className={styles.chartHeader}>
                                <h3>Trend</h3>
                                <div className={styles.chartControls}>
                                    <Radio.Group
                                        size="small"
                                        optionType="button"
                                        buttonStyle="solid"
                                        value={chartType}
                                        onChange={e => setChartType(e.target.value)}
                                        options={chartTypeOptions}
                                    />
                                    <Select
                                        size="small"
                                        value={chartGroupBy}
                                        onChange={setChartGroupBy}
                                        options={chartMetric === "tokens" ? groupByOptionsForTokens : groupByOptions}
                                        style={{width: 150}}
                                    />
                                    <Radio.Group
                                        size="small"
                                        optionType="button"
                                        buttonStyle="solid"
                                        value={chartMetric}
                                        onChange={e => onChartMetricChange(e.target.value)}
                                        options={chartMetricOptions}
                                    />
                                </div>
                            </div>
                            <div className={styles.chartContainer}>
                                {needsGroupedData ? renderGroupedChart() : renderTokensByTypeChart()}
                                {renderChartLegend()}
                            </div>
                        </div>
                    )}

                    <div className={styles.section}>
                        <h3>{timePeriod === "month" ? "Monthly" : "Daily"}</h3>
                        <Table rowKey="date" pagination={false} size="small" showSorterTooltip={false} dataSource={daily} columns={dailyColumns()}/>
                    </div>

                    <div className={styles.section}>
                        <Tabs items={[
                            {
                                key: "purpose",
                                label: "By purpose",
                                children: (
                                    <Table
                                        rowKey={record => record.aiConfigurationKind || record.label}
                                        pagination={false}
                                        size="small"
                                        showSorterTooltip={false}
                                        dataSource={purposeBreakdown}
                                        columns={purposeColumns()}
                                        summary={() => renderSummaryRow(purposeBreakdown, 1)}
                                    />
                                )
                            },
                            ...(props.showOrganizationBreakdown && !selectedOrgId ? [{
                                key: "organization",
                                label: "By organization",
                                children: (
                                    <Table
                                        rowKey={record => record.organizationId || record.label}
                                        pagination={false}
                                        size="small"
                                        showSorterTooltip={false}
                                        dataSource={organizationBreakdown}
                                        columns={organizationColumns()}
                                        summary={() => renderSummaryRow(organizationBreakdown, 1)}
                                    />
                                )
                            }] : []),
                            {
                                key: "source",
                                label: "By source",
                                children: (
                                    <>
                                        {showSourceOrganizationGroupingControl() && (
                                            <div className={styles.tableControls}>
                                                <span className={styles.tableControlLabel}>Group by</span>
                                                <Radio.Group
                                                    size="small"
                                                    optionType="button"
                                                    buttonStyle="solid"
                                                    value={sourceBreakdownGroupBy}
                                                    onChange={e => setSourceBreakdownGroupBy(e.target.value)}
                                                    options={[
                                                        {value: "source", label: "Source"},
                                                        {value: "source_organization", label: "Source + organization"},
                                                    ]}
                                                />
                                            </div>
                                        )}
                                        <Table
                                            key={sourceBreakdownGroupBy}
                                            rowKey={sourceRowKey}
                                            pagination={false}
                                            size="small"
                                            loading={sourceBreakdownLoading}
                                            showSorterTooltip={false}
                                            dataSource={activeSourceBreakdown()}
                                            columns={sourceColumns(activeSourceBreakdown(), sourceBreakdownGroupBy)}
                                            summary={() => renderSummaryRow(activeSourceBreakdown(), sourceLabelColumnCount(sourceBreakdownGroupBy))}
                                        />
                                    </>
                                )
                            },
                            {
                                key: "model",
                                label: "By model",
                                children: (
                                    <Table
                                        rowKey={record => record.model || record.label}
                                        pagination={false}
                                        size="small"
                                        showSorterTooltip={false}
                                        dataSource={modelBreakdown}
                                        columns={modelColumns()}
                                        summary={() => renderSummaryRow(modelBreakdown, 2)}
                                    />
                                )
                            },
                            {
                                key: "ai-extractor",
                                label: "By AI extractor",
                                children: (
                                    <Table
                                        rowKey={record => record.aiExtractorId || record.label}
                                        pagination={false}
                                        size="small"
                                        showSorterTooltip={false}
                                        dataSource={aiExtractorBreakdown}
                                        columns={aiExtractorColumns()}
                                        summary={() => renderSummaryRow(aiExtractorBreakdown, 3)}
                                    />
                                )
                            },
                            {
                                key: "ai-report-summarizer",
                                label: "By AI summarizer",
                                children: (
                                    <Table
                                        rowKey={record => record.aiConfigurationId || record.label}
                                        pagination={false}
                                        size="small"
                                        showSorterTooltip={false}
                                        dataSource={aiReportSummarizerBreakdown}
                                        columns={aiReportSummarizerColumns()}
                                        summary={() => renderSummaryRow(aiReportSummarizerBreakdown, 3)}
                                    />
                                )
                            }
                        ]}/>
                    </div>
                </>
            )}
        </Spin>
    );

    function reload() {
        const requestId = ++reloadRequestId.current;

        setLoading(true);

        const query = {
            dateFrom: dateFromStr,
            dateTo: dateToStr,
            organizationId: effectiveOrgId
        };
        const timeSeriesQuery = {
            ...query,
            period: timePeriod
        };

        const rangeDays = range[1].diff(range[0], "day") + 1;
        const prevTo = range[0].subtract(1, "day");
        const prevFrom = prevTo.subtract(rangeDays - 1, "day");

        Promise.all([
            aiUsageService.getSummary(query),
            aiUsageService.getDaily(timeSeriesQuery),
            aiUsageService.getBreakdown(query, "model"),
            aiUsageService.getBreakdown(query, "ai_configuration_kind"),
            aiUsageService.getBreakdown(query, "ai_extractor"),
            aiUsageService.getBreakdown(query, "ai_report_summarizer"),
            props.showOrganizationBreakdown && !selectedOrgId
                ? aiUsageService.getBreakdown(query, "organization")
                : Promise.resolve([] as AiUsageBreakdownRow[]),
            aiUsageService.getSummary({
                dateFrom: prevFrom.format("YYYY-MM-DD"),
                dateTo: prevTo.format("YYYY-MM-DD"),
                organizationId: effectiveOrgId
            }),
        ])
            .then(([summaryResult, dailyResult, modelResult, purposeResult, aiExtractorResult, aiReportSummarizerResult, orgResult, prevSummaryResult]) => {
                if (reloadRequestId.current !== requestId) {
                    return;
                }

                setSummary(summaryResult as AiUsageSummary);
                setDaily(dailyResult as AiUsageDailyPoint[]);
                setModelBreakdown(modelResult as AiUsageBreakdownRow[]);
                setPurposeBreakdown(purposeResult as AiUsageBreakdownRow[]);
                setAiExtractorBreakdown(aiExtractorResult as AiUsageBreakdownRow[]);
                setAiReportSummarizerBreakdown(aiReportSummarizerResult as AiUsageBreakdownRow[]);
                setOrganizationBreakdown(orgResult as AiUsageBreakdownRow[]);
                setPreviousPrice((prevSummaryResult as AiUsageSummary).price);
            })
            .finally(() => {
                if (reloadRequestId.current === requestId) {
                    setLoading(false);
                }
            });
    }

    function reloadSourceBreakdown() {
        const requestId = ++sourceBreakdownRequestId.current;
        const groupBy = sourceBreakdownGroupBy;

        setSourceBreakdownLoading(true);

        aiUsageService.getBreakdown({
            dateFrom: dateFromStr,
            dateTo: dateToStr,
            organizationId: effectiveOrgId
        }, groupBy)
            .then(rows => {
                if (sourceBreakdownRequestId.current === requestId) {
                    setActiveSourceBreakdown(groupBy, rows);
                }
            })
            .finally(() => {
                if (sourceBreakdownRequestId.current === requestId) {
                    setSourceBreakdownLoading(false);
                }
            });
    }

    function metricTickFormat(metric: ChartMetric): (tick: number) => string {
        if (metric === "price") {
            return formatCredits;
        }

        const formatter = new Intl.NumberFormat(undefined, {notation: "compact"});

        return (tick) => formatter.format(tick);
    }

    function groupedMetricKey(label: string, metric: ChartMetric): string {
        return label + GROUPED_METRIC_SUFFIX[metric];
    }

    function tooltipRow(color: string, label: string, value: string) {
        return `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">` +
            `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color};flex-shrink:0"></span>` +
            `<span style="flex:1">${label}</span>` +
            `<span style="font-weight:500;margin-left:12px">${value}</span></div>`;
    }

    function tooltipWrap(date: string, rows: string, total?: string) {
        return `<div style="font-size:12px;padding:6px 10px;min-width:160px">` +
            `<div style="font-weight:600;margin-bottom:4px;border-bottom:1px solid #f0f0f0;padding-bottom:4px">${date}</div>` +
            rows +
            (total ? `<div style="font-weight:600;margin-top:4px;border-top:1px solid #f0f0f0;padding-top:4px;text-align:right">${total}</div>` : "") +
            `</div>`;
    }

    function chartXValue(dataPoint: { date: string }) {
        return new Date(dataPoint.date).getTime();
    }

    function barDataStep() {
        return timePeriod === "day" ? DAY_MS : undefined;
    }

    function formatChartDate(date: string | number) {
        const {full} = chartDateFormats[timePeriod];

        return dayjs(date).format(full);
    }

    function formatChartTick(tick: number) {
        const {tick: tickFormat} = chartDateFormats[timePeriod];

        return dayjs(tick).format(tickFormat);
    }

    function renderChartLegend() {
        if (chartMetric === "tokens" && chartGroupBy === "type") {
            return (
                <div className={styles.chartLegend}>
                    <VisBulletLegend items={tokenTypeLegendItems}/>
                </div>
            );
        }

        if (needsGroupedData && groupedLegendItems.length > 0) {
            return (
                <div className={styles.chartLegend}>
                    <VisBulletLegend items={groupedLegendItems}/>
                </div>
            );
        }

        return null;
    }

    function renderTokensByTypeChart() {
        const typeColors = tokenTypeLegendItems.map(i => i.color);
        const typeLabels = tokenTypeLegendItems.map(i => i.name);

        return (
            <VisXYContainer<AiUsageDailyPoint> data={daily} height={250}>
                {renderTokensByTypeSeries(typeColors)}
                <VisCrosshair<AiUsageDailyPoint>
                    template={(d: AiUsageDailyPoint) => {
                        const values = [d.inputTokens - d.inputCachedTokens, d.inputCachedTokens, d.outputTokens];
                        const rows = typeLabels.map((label, i) => tooltipRow(typeColors[i], label, formatInteger(values[i]))).join("");

                        return tooltipWrap(formatChartDate(d.date), rows, "Total: " + formatInteger(d.totalTokens));
                    }}
                />
                <VisTooltip/>
                <VisAxis<AiUsageDailyPoint>
                    type="x"
                    tickFormat={formatChartTick}
                    numTicks={Math.min(daily.length, 10)}
                />
                <VisAxis<AiUsageDailyPoint>
                    type="y"
                    tickFormat={metricTickFormat("tokens")}
                />
            </VisXYContainer>
        );
    }

    function renderTokensByTypeSeries(colors: string[]) {
        const yAccessors = [
            (d: AiUsageDailyPoint) => d.inputTokens - d.inputCachedTokens,
            (d: AiUsageDailyPoint) => d.inputCachedTokens,
            (d: AiUsageDailyPoint) => d.outputTokens,
        ];

        if (chartType === "line") {
            return (
                <VisLine<AiUsageDailyPoint>
                    x={chartXValue}
                    y={yAccessors}
                    color={(_: AiUsageDailyPoint[], i: number) => colors[i]}
                    curveType={CurveType.Linear}
                    lineWidth={2}
                />
            );
        }

        if (chartType === "area") {
            return (
                <VisArea<AiUsageDailyPoint>
                    x={chartXValue}
                    y={yAccessors}
                    color={(_: AiUsageDailyPoint[], i: number) => colors[i]}
                    curveType={CurveType.Linear}
                    opacity={0.7}
                />
            );
        }

        return (
            <VisStackedBar<AiUsageDailyPoint>
                x={chartXValue}
                y={yAccessors}
                color={(_: AiUsageDailyPoint, i: number) => colors[i]}
                dataStep={barDataStep()}
                barPadding={0.2}
                roundedCorners={2}
            />
        );
    }

    function renderGroupedChart() {
        if (groupedChartData.length === 0) {
            return null;
        }

        const colors = groupedLabels.map((_, i) => PALETTE[i % PALETTE.length]);
        const yAccessors = groupedLabels.map(label => (d: GroupedChartRow) => Number(d[groupedMetricKey(label, chartMetric)] ?? 0));
        const valueFormatter = chartMetric === "price" ? formatCredits : formatInteger;

        return (
            <VisXYContainer<GroupedChartRow> data={groupedChartData} height={250}>
                {renderGroupedSeries(yAccessors, colors)}
                <VisCrosshair<GroupedChartRow>
                    template={(d: GroupedChartRow) => {
                        let total = 0;
                        let rows = "";

                        groupedLabels.forEach((label, i) => {
                            const val = Number(d[groupedMetricKey(label, chartMetric)] ?? 0);

                            total += val;
                            rows += tooltipRow(colors[i], label, valueFormatter(val));
                        });

                        return tooltipWrap(formatChartDate(d.date), rows, "Total: " + valueFormatter(total));
                    }}
                />
                <VisTooltip/>
                <VisAxis<GroupedChartRow>
                    type="x"
                    tickFormat={formatChartTick}
                    numTicks={Math.min(groupedChartData.length, 10)}
                />
                <VisAxis<GroupedChartRow>
                    type="y"
                    tickFormat={metricTickFormat(chartMetric)}
                />
            </VisXYContainer>
        );
    }

    function renderGroupedSeries(yAccessors: ((d: GroupedChartRow) => number)[], colors: string[]) {
        if (chartType === "line") {
            return (
                <VisLine<GroupedChartRow>
                    x={chartXValue}
                    y={yAccessors}
                    color={(_: GroupedChartRow[], i: number) => colors[i]}
                    curveType={CurveType.Linear}
                    lineWidth={2}
                />
            );
        }

        if (chartType === "area") {
            return (
                <VisArea<GroupedChartRow>
                    x={chartXValue}
                    y={yAccessors}
                    color={(_: GroupedChartRow[], i: number) => colors[i]}
                    curveType={CurveType.Linear}
                    opacity={0.7}
                />
            );
        }

        return (
            <VisStackedBar<GroupedChartRow>
                x={chartXValue}
                y={yAccessors}
                color={(_: GroupedChartRow, i: number) => colors[i]}
                dataStep={barDataStep()}
                barPadding={0.2}
                roundedCorners={2}
            />
        );
    }

    function dailyColumns(): ColumnsType<AiUsageDailyPoint> {
        return [
            {
                dataIndex: "date",
                title: timePeriod === "month" ? "Month" : "Date",
                width: 160,
                defaultSortOrder: "ascend",
                sortDirections: ascendingFirstSortDirections,
                render: value => formatChartDate(value),
                sorter: (a, b) => a.date.localeCompare(b.date)
            },
            {dataIndex: "requestCount", title: "Requests", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.requestCount - b.requestCount},
            {dataIndex: "apiCallCount", title: "API calls", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.apiCallCount - b.apiCallCount},
            {dataIndex: "webSearchCallCount", title: "Web search", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.webSearchCallCount - b.webSearchCallCount},
            {dataIndex: "inputTokens", title: "Input", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.inputTokens - b.inputTokens},
            {dataIndex: "inputCachedTokens", title: "Input cached", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.inputCachedTokens - b.inputCachedTokens},
            {dataIndex: "outputTokens", title: "Output", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.outputTokens - b.outputTokens},
            {dataIndex: "totalTokens", title: "Total", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.totalTokens - b.totalTokens},
            {dataIndex: "executionTimeMs", title: "Exec time", align: "right", render: value => formatDurationMs(value), sorter: (a, b) => a.executionTimeMs - b.executionTimeMs},
            {
                dataIndex: "price",
                title: "Credits",
                align: "right",
                render: value => <CreditsValue value={value} showIcon={false}/>,
                sorter: (a, b) => a.price - b.price
            }
        ];
    }

    function priceDelta(): { text: string; direction: "up" | "down" } | undefined {
        if (previousPrice === undefined || !summary || previousPrice === 0) {
            return undefined;
        }

        const delta = ((summary.price - previousPrice) / previousPrice) * 100;
        const sign = delta >= 0 ? "+" : "";

        return {text: `${sign}${delta.toFixed(1)}%`, direction: delta >= 0 ? "up" : "down"};
    }

    function summaryCardGroups() {
        const cacheHitRatio = summary && summary.inputTokens > 0
            ? ((summary.inputCachedTokens / summary.inputTokens) * 100).toFixed(1) + "%"
            : "n/a";
        const avgCreditsPerRequest = summary && summary.requestCount > 0
            ? formatCredits(summary.price / summary.requestCount)
            : "n/a";
        const delta = priceDelta();

        return [
            {
                label: "Volume",
                cards: [
                    {key: "requests", label: "Requests", value: formatInteger(summary?.requestCount ?? 0)},
                    {key: "executionTimeMs", label: "Execution time", value: formatDurationMs(summary?.executionTimeMs ?? 0)},
                ]
            },
            {
                label: "Tokens",
                cards: [
                    {key: "inputTokens", label: "Input tokens", value: formatCompactInteger(summary?.inputTokens ?? 0), title: formatInteger(summary?.inputTokens ?? 0)},
                    {key: "cacheHitRatio", label: "Cache hit ratio", value: cacheHitRatio},
                    {key: "outputTokens", label: "Output tokens", value: formatCompactInteger(summary?.outputTokens ?? 0), title: formatInteger(summary?.outputTokens ?? 0)},
                ]
            },
            {
                label: "Credits",
                cards: [
                    {key: "price", label: "Spent", value: <CreditsValue value={summary?.price ?? 0}/>, delta},
                    {key: "avgCreditsPerRequest", label: "Avg / request", value: avgCreditsPerRequest},
                ]
            }
        ];
    }

    function organizationColumns(): ColumnsType<AiUsageBreakdownRow> {
        return [
            {
                dataIndex: "label",
                title: "Organization",
                render: (value, row) => row.organizationId ? <Link to={`/organizations/${row.organizationId}`}>{value}</Link> : value,
                sorter: (a, b) => compareText(a.label, b.label)
            },
            ...commonBreakdownMetricColumns(organizationBreakdown)
        ];
    }

    function modelColumns(): ColumnsType<AiUsageBreakdownRow> {
        return [
            {dataIndex: "aiExtractorType", title: "Type", render: value => formatAiExtractorType(value), sorter: (a, b) => compareText(formatAiExtractorType(a.aiExtractorType), formatAiExtractorType(b.aiExtractorType))},
            {dataIndex: "model", title: "Model", sorter: (a, b) => compareText(a.model, b.model)},
            ...commonBreakdownMetricColumns(modelBreakdown)
        ];
    }

    function purposeColumns(): ColumnsType<AiUsageBreakdownRow> {
        return [
            {
                dataIndex: "label",
                title: "Purpose",
                render: (_, row) => formatAiUsagePurpose(row.aiConfigurationKind, row.label),
                sorter: (a, b) => compareText(formatAiUsagePurpose(a.aiConfigurationKind, a.label), formatAiUsagePurpose(b.aiConfigurationKind, b.label))
            },
            ...commonBreakdownMetricColumns(purposeBreakdown)
        ];
    }

    function activeSourceBreakdown() {
        return sourceBreakdownGroupBy === "source_organization" ? sourceOrganizationBreakdown : sourceBreakdown;
    }

    function setActiveSourceBreakdown(groupBy: SourceBreakdownGroupBy, rows: AiUsageBreakdownRow[]) {
        if (groupBy === "source_organization") {
            setSourceOrganizationBreakdown(rows);
        } else {
            setSourceBreakdown(rows);
        }
    }

    function sourceColumns(data: AiUsageBreakdownRow[], groupBy: SourceBreakdownGroupBy): ColumnsType<AiUsageBreakdownRow> {
        const columns: ColumnsType<AiUsageBreakdownRow> = [
            {
                dataIndex: "label",
                title: "Source",
                render: (value, row) => row.sourceId ? <Link to={`/vul-sources/${row.sourceId}`}>{value}</Link> : value,
                sorter: (a, b) => compareText(a.label, b.label)
            },
            {dataIndex: "sourceType", title: "Type", render: value => formatVulSourceType(value), sorter: (a, b) => compareText(a.sourceType, b.sourceType)},
        ];

        if (showSourceOrganizationGroupingControl() && groupBy === "source_organization") {
            columns.push({dataIndex: "organizationName", title: "Organization", sorter: (a, b) => compareText(a.organizationName, b.organizationName)});
        }

        return [
            ...columns,
            ...commonBreakdownMetricColumns(data)
        ];
    }

    function sourceLabelColumnCount(groupBy: SourceBreakdownGroupBy) {
        return showSourceOrganizationGroupingControl() && groupBy === "source_organization" ? 3 : 2;
    }

    function showSourceOrganizationGroupingControl() {
        return props.showOrganizationBreakdown && !selectedOrgId;
    }

    function sourceRowKey(record: AiUsageBreakdownRow) {
        const sourceKey = record.sourceId || `${record.label}:${record.sourceType || ""}`;

        if (sourceBreakdownGroupBy === "source_organization") {
            return `${sourceKey}:${record.organizationId || "system"}`;
        }

        return sourceKey;
    }

    function aiExtractorColumns(): ColumnsType<AiUsageBreakdownRow> {
        return [
            {
                dataIndex: "label",
                title: "AI extractor",
                render: (value, row) => row.aiExtractorId
                    ? <Link to={`/definitions/ai-extractors/${row.aiExtractorId}`}>{value}</Link>
                    : value,
                sorter: (a, b) => compareText(a.label, b.label)
            },
            {dataIndex: "aiExtractorType", title: "Type", render: value => formatAiExtractorType(value), sorter: (a, b) => compareText(formatAiExtractorType(a.aiExtractorType), formatAiExtractorType(b.aiExtractorType))},
            {dataIndex: "model", title: "Model", sorter: (a, b) => compareText(a.model, b.model)},
            ...commonBreakdownMetricColumns(aiExtractorBreakdown)
        ];
    }

    function aiReportSummarizerColumns(): ColumnsType<AiUsageBreakdownRow> {
        return [
            {
                dataIndex: "label",
                title: "AI report summarizer",
                render: (value, row) => row.aiConfigurationId
                    ? <Link to={`/definitions/ai-report-summarizers/${row.aiConfigurationId}`}>{value}</Link>
                    : value,
                sorter: (a, b) => compareText(a.label, b.label)
            },
            {dataIndex: "aiExtractorType", title: "Type", render: value => formatAiExtractorType(value), sorter: (a, b) => compareText(formatAiExtractorType(a.aiExtractorType), formatAiExtractorType(b.aiExtractorType))},
            {dataIndex: "model", title: "Model", sorter: (a, b) => compareText(a.model, b.model)},
            ...commonBreakdownMetricColumns(aiReportSummarizerBreakdown)
        ];
    }

    function formatAiUsagePurpose(kind?: string, fallback?: string) {
        switch (kind) {
            case "AI_EXTRACTOR":
                return "AI extractors";
            case "AI_REPORT_SUMMARIZER":
                return "AI report summaries";
            default:
                return fallback || kind || "";
        }
    }

    function compareText(a?: string, b?: string) {
        return (a ?? "").localeCompare(b ?? "");
    }

    function commonBreakdownMetricColumns(data: AiUsageBreakdownRow[]): ColumnsType<AiUsageBreakdownRow> {
        const totalPrice = data.reduce((sum, row) => sum + row.price, 0);

        return [
            {dataIndex: "requestCount", title: "Requests", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.requestCount - b.requestCount},
            {dataIndex: "apiCallCount", title: "API calls", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.apiCallCount - b.apiCallCount},
            {dataIndex: "webSearchCallCount", title: "Web search", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.webSearchCallCount - b.webSearchCallCount},
            {dataIndex: "inputTokens", title: "Input", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.inputTokens - b.inputTokens},
            {dataIndex: "inputCachedTokens", title: "Input cached", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.inputCachedTokens - b.inputCachedTokens},
            {dataIndex: "outputTokens", title: "Output", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.outputTokens - b.outputTokens},
            {dataIndex: "totalTokens", title: "Total", align: "right", render: value => formatInteger(value), sorter: (a, b) => a.totalTokens - b.totalTokens},
            {dataIndex: "executionTimeMs", title: "Exec time", align: "right", render: value => formatDurationMs(value), sorter: (a, b) => a.executionTimeMs - b.executionTimeMs},
            {
                dataIndex: "price",
                title: "Credits",
                align: "right",
                defaultSortOrder: "descend",
                render: value => <CreditsValue value={value} showIcon={false}/>,
                sorter: (a, b) => a.price - b.price
            },
            {
                key: "pricePercent", title: "%", align: "right", width: 65,
                render: (_: unknown, row: AiUsageBreakdownRow) => {
                    const pct = totalPrice > 0 ? (row.price / totalPrice) * 100 : 0;
                    return <span className={styles.pricePercent}>{pct.toFixed(1)}%</span>;
                },
                sorter: (a, b) => a.price - b.price
            }
        ];
    }

    function renderSummaryRow(data: AiUsageBreakdownRow[], labelColumnCount: number) {
        if (data.length === 0) {
            return null;
        }

        const totals = data.reduce((acc, row) => ({
            requestCount: acc.requestCount + row.requestCount,
            apiCallCount: acc.apiCallCount + row.apiCallCount,
            webSearchCallCount: acc.webSearchCallCount + row.webSearchCallCount,
            inputTokens: acc.inputTokens + row.inputTokens,
            inputCachedTokens: acc.inputCachedTokens + row.inputCachedTokens,
            outputTokens: acc.outputTokens + row.outputTokens,
            totalTokens: acc.totalTokens + row.totalTokens,
            executionTimeMs: acc.executionTimeMs + row.executionTimeMs,
            price: acc.price + row.price,
        }), {requestCount: 0, apiCallCount: 0, webSearchCallCount: 0, inputTokens: 0, inputCachedTokens: 0, outputTokens: 0, totalTokens: 0, executionTimeMs: 0, price: 0});

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={labelColumnCount}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount} align="right"><strong>{formatInteger(totals.requestCount)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount + 1} align="right"><strong>{formatInteger(totals.apiCallCount)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount + 2} align="right"><strong>{formatInteger(totals.webSearchCallCount)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount + 3} align="right"><strong>{formatInteger(totals.inputTokens)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount + 4} align="right"><strong>{formatInteger(totals.inputCachedTokens)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount + 5} align="right"><strong>{formatInteger(totals.outputTokens)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount + 6} align="right"><strong>{formatInteger(totals.totalTokens)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount + 7} align="right"><strong>{formatDurationMs(totals.executionTimeMs)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount + 8} align="right"><strong><CreditsValue value={totals.price} showIcon={false}/></strong></Table.Summary.Cell>
                <Table.Summary.Cell index={labelColumnCount + 9} align="right"><strong className={styles.pricePercent}>100.0%</strong></Table.Summary.Cell>
            </Table.Summary.Row>
        );
    }

}

function aiUsagePersistentIdent(organizationId?: string) {
    return organizationId ? `${PERSISTENT_IDENT}.${organizationId}` : PERSISTENT_IDENT;
}

function loadAiUsageFilterValues(searchParams: URLSearchParams, filterFormPersister: FormPersister, showOrganizationFilter?: boolean): AiUsageFilterValues {
    if (hasAiUsageFilterSearchParams(searchParams, showOrganizationFilter)) {
        return aiUsageFilterValuesFromSearchParams(searchParams, showOrganizationFilter);
    }

    return normalizeAiUsageFilterValues(filterFormPersister.loadValues());
}

function hasAiUsageFilterSearchParams(searchParams: URLSearchParams, showOrganizationFilter?: boolean) {
    return searchParams.has("range")
        || searchParams.has("from")
        || searchParams.has("to")
        || searchParams.has("period")
        || (showOrganizationFilter && searchParams.has("organizationId"));
}

function aiUsageFilterValuesFromSearchParams(searchParams: URLSearchParams, showOrganizationFilter?: boolean): AiUsageFilterValues {
    const defaults = createDefaultAiUsageFilterValues();
    const presetId = normalizeDateRangePresetId(searchParams.get("range"));
    const presetRange = resolveDateRangePresetValue(presetId);

    return {
        dateFrom: presetRange?.[0] ?? parseFilterDate(searchParams.get("from"), defaults.dateFrom),
        dateTo: presetRange?.[1] ?? parseFilterDate(searchParams.get("to"), defaults.dateTo),
        dateRangePresetId: presetRange ? presetId : undefined,
        period: normalizeAiUsageTimePeriod(searchParams.get("period")),
        organizationId: showOrganizationFilter ? searchParams.get("organizationId") ?? undefined : undefined
    };
}

function normalizeAiUsageFilterValues(values: Partial<AiUsageFilterValues>): AiUsageFilterValues {
    const defaults = createDefaultAiUsageFilterValues();
    const presetId = normalizeDateRangePresetId(values.dateRangePresetId);
    const presetRange = resolveDateRangePresetValue(presetId);

    return {
        dateFrom: presetRange?.[0] ?? parseFilterDate(values.dateFrom, defaults.dateFrom),
        dateTo: presetRange?.[1] ?? parseFilterDate(values.dateTo, defaults.dateTo),
        dateRangePresetId: presetRange ? presetId : undefined,
        period: normalizeAiUsageTimePeriod(values.period),
        organizationId: values.organizationId
    };
}

function createDefaultAiUsageFilterValues(): AiUsageFilterValues {
    const dateRangePresetId = "last-30-days";
    const range = resolveDateRangePresetValue(dateRangePresetId)!;

    return {
        dateFrom: range[0],
        dateTo: range[1],
        dateRangePresetId,
        period: "day"
    };
}

function normalizeDateRangePresetId(value: unknown): DateRangePresetId | undefined {
    return resolveDateRangePresetValue(value) ? value as DateRangePresetId : undefined;
}

function normalizeAiUsageTimePeriod(value: unknown): AiUsageTimePeriod {
    return typeof value === "string" && value.toLowerCase() === "month" ? "month" : "day";
}

export default AiUsageOverview;
